import { ChangeDetectionStrategy, Component, computed, effect, inject, Injector, Type } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ComponentOutletsComponent, LoadingSkeletonDirective2, RenderInService, RenderTargetDirective } from 'common-ui';
import { ItemStockComponentStore } from 'products-data';
import { ItemComponentBase } from '../../../ItemComponentBase';
import { ErrorComponent } from 'error-ui';
import { MatListModule } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { branchNameLocalizations } from 'branches-data';
import { ItemStockLayout } from './item-stock-layout';

@Component({
  selector: 'ideal-item-stock',
  imports: [
    RenderTargetDirective,
    ComponentOutletsComponent,
    LoadingSkeletonDirective2,
    ErrorComponent,
    DecimalPipe,
    MatListModule,
    MatIcon,
  ],
  standalone: true,
  templateUrl: './item-stock.component.html',
  styleUrl: './item-stock.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RenderInService, ItemStockComponentStore],
})
export class ItemStockComponent extends ItemComponentBase {
  protected readonly Slots = ItemStockLayout.Slots;

  private readonly _injector = inject(Injector);
  protected readonly providers = computed<Type<unknown>[]>(() => {
    const type = this.productType();
    if (!type) return [];
    const token = ItemStockLayout.Tokens[type];
    return token ? this._injector.get(token, []) : [];
  });
  protected readonly componentInputs = computed(() => ({ itemId: this.id() }));

  private readonly _stockStore = inject(ItemStockComponentStore);
  protected readonly stock = this._stockStore.itemStock;

  protected readonly branches = computed(
    () =>
      this.stock()?.stock.map((b) => ({
        ...b,
        branchName: branchNameLocalizations[+b.branchId as keyof typeof branchNameLocalizations] ?? b.branchName.toLowerCase(),
      })) ?? [],
  );
  protected readonly branchesSorted = computed(() =>
    this.branches()
      .slice()
      .sort((a, b) => {
        if (a.isHomeBranch !== b.isHomeBranch) return a.isHomeBranch ? -1 : 1;
        const aHasStock = a.quantity > 0;
        const bHasStock = b.quantity > 0;
        if (aHasStock !== bHasStock) return aHasStock ? -1 : 1;
        return (a.branchName ?? a.branchId).localeCompare(b.branchName ?? b.branchId);
      }),
  );
  protected readonly total = computed(() => this.branches().reduce((acc, b) => acc + b.quantity, 0));

  constructor() {
    super();
    effect(() => {
      this._stockStore.selectProductById(this.id());
    });
  }
}
